import React from 'react';
import { computeRowForOneDeliveryDetail } from '../../utils/silo.util';
import AgrivitechCheckbox from '../agv-checkbox/agv-checkbox';
import AlimentTypeTag from '../fleet-aliment-type-tag/aliment-type-tag';
import { ALIMENT_TYPE_ENUM } from '../fleet-aliment-type-tag/aliment-type.enum';
import { BL_STATUS, PACKAGING_TYPE } from '../../enums/bl.enum';
import { WEIGHT_UNIT_ENUM } from '../fleet-weight-unit/weight-unit.enum';

export default function Products({ bl, features, client_id }) {
  const allProducts = [];

  for (const item of bl.items) {
    const silosPerCompartment = computeRowForOneDeliveryDetail(
      bl,
      item,
      features,
      client_id
    );

    if (silosPerCompartment?.length > 0) {
      const silosAndProductsDiv = silosPerCompartment?.map?.((row, index) => (
        <div key={`${row.what}_${row.where}_${index}`}>
          <SingleSiloDisplay row={row} bl={bl} />
          <ProductDisplay row={row} item={item} />
        </div>
      ));

      // ! TODO : Peut être split un jour l'affichage et les fonctions entre le cas des quantités manquantes au niveau du BL, et des quantités manquantes au niveau du produit.
      allProducts.push(
        <>
          {silosAndProductsDiv}
          <MissingProductQuantity
            reserveAssociatedWithProduct={
              item?.reserves?.[item?.reserves?.length - 1]
            }
            silosPerCompartment={silosPerCompartment}
            bl={bl}
            item={item}
          />
        </>
      );
    } else {
      allProducts.push(
        <>
          <ProductDisplay key={`product_${item.id}`} item={item} />
          <MissingProductQuantity
            reserveAssociatedWithProduct={
              item?.reserves?.[item?.reserves?.length - 1]
            }
            bl={bl}
            item={item}
          />
        </>
      );
    }
  }

  return <>{allProducts}</>;
}

function determineDataToDisplay(item, quantityOverride) {
  //! TODO : Refactor ça un jour, c'est horrible
  if (item?.client_id === 6) {
    // Pour Moulins dumée, l'affichage entre le champ poids / quantités change en fonction du type de produit (VRAC, SAC, PICKING)
    if (item?.packaging === ALIMENT_TYPE_ENUM.VRAC) {
      return `${quantityOverride || item?.net_weight} ${item?.weight_unit}`;
    } else if (item?.packaging === ALIMENT_TYPE_ENUM.SAC) {
      return `${item?.quantity} Sac${item?.quantity > 1 ? 's' : ''}`;
    } else if (item?.packaging === ALIMENT_TYPE_ENUM.PICKING) {
      return `${item?.quantity} Unité${item?.quantity > 1 ? 's' : ''}`;
    }
  }
  if (item?.client_id === 1) {
    const unitWhereQuantityIsDisplayed = [
      WEIGHT_UNIT_ENUM.DOS,
      WEIGHT_UNIT_ENUM.PCE,
      WEIGHT_UNIT_ENUM.NAR,
    ];
    if (
      item?.weight_unit &&
      unitWhereQuantityIsDisplayed.includes(item?.weight_unit.toUpperCase())
    ) {
      return `${item.quantity || 1} ${item?.weight_unit}`;
    } else {
      if (item?.net_weight) {
        return `${item?.net_weight} ${item?.weight_unit}`;
      } else {
        return `${item?.quantity || 1} ${item?.weight_unit || 'Unité'}`;
      }
    }
  } else {
    // Pour les autres clients, on affiche toujours le poids si il est présent, sinon la quantité.
    if (item?.net_weight) {
      return `${item?.net_weight} ${item?.weight_unit}`;
    } else {
      return `${item?.quantity || 1} ${item?.weight_unit || 'Unité'}`;
    }
  }
}

function ProductDisplay({ row, item }) {
  let siloDeliveredObjectIfAny = '';

  if (row?.where_object?.is_replaced && row?.where_object?.replaced_by) {
    siloDeliveredObjectIfAny = row.where_object.replaced_by;
  } else if (row?.where_object?.is_planned) {
    siloDeliveredObjectIfAny = row.where_object;
  } else if (row?.where_object?.is_delivered) {
    siloDeliveredObjectIfAny = row.where_object;
  }

  let quantityDelivered = item?.net_weight;
  if (
    item?.packaging === ALIMENT_TYPE_ENUM.VRAC &&
    item?.reserves?.[item?.reserves?.length - 1]?.additional_datas
      ?.nb_qty_delivered
  ) {
    quantityDelivered =
      item.reserves[item?.reserves?.length - 1].additional_datas
        .nb_qty_delivered;
  }

  let productWeightDisplayed = 'nc.';

  if (siloDeliveredObjectIfAny?.delivered_quantity != undefined) {
    productWeightDisplayed = `${siloDeliveredObjectIfAny.delivered_quantity.toFixed(2)} / ${item?.net_weight} ${item?.weight_unit}`;
  } else if (quantityDelivered !== item?.net_weight) {
    productWeightDisplayed = determineDataToDisplay(item, quantityDelivered);
  } else if (item?.net_weight || item?.quantity) {
    productWeightDisplayed = determineDataToDisplay(item);
  }

  const productDiv = (
    <div className="tw-self-stretch tw-flex tw-flex-row tw-items-center tw-justify-between tw-text-text-dark-medium-importance">
      <div className="tw-flex tw-flex-row tw-gap-2 tw-items-center">
        <AlimentTypeTag
          item={
            Object.values(ALIMENT_TYPE_ENUM).includes(
              item?.type?.toUpperCase?.()
            )
              ? item?.type?.toUpperCase()
              : ALIMENT_TYPE_ENUM.AUTRES
          }
        />
        {item?.name
          ? item?.client_id !== 6
            ? item.name
            : `${item.name}${item.delivery_type === 'REPRISE' ? ' [REPRISE]' : ''}`
          : 'PRODUCT_NAME'}
      </div>

      <div className="tw-flex-shrink-0 tw-text-right tw-min-w-[80px]">
        {productWeightDisplayed}
      </div>
    </div>
  );

  const subItemsDiv = item?.sub_items?.map((sub_item) => {
    return <SubItemDisplay key={sub_item?.id} sub_item={sub_item} />;
  });

  const itemDescription = item?.description && (
    <>
      <div
        key={`item_description_${item?.id}`}
        className="tw-self-stretch tw-flex tw-flex-row tw-items-center tw-italic tw-justify-between tw-text-text-dark-medium-importance"
      >
        <div className="tw-flex tw-flex-row tw-gap-2 tw-ml-[42px] tw-items-center">
          <div className="tw-text-wrap">{item?.description}</div>
        </div>
      </div>
    </>
  );

  const reserveInformation =
    item?.client_id === 6 &&
    item?.reserves?.length > 0 &&
    renderAllReservesForMoulinsDumee(item);

  return (
    <>
      {productDiv}
      {subItemsDiv}
      {itemDescription}
      {reserveInformation}
    </>
  );
}

function SubItemDisplay({ sub_item }) {
  return (
    <div
      key={sub_item?.id}
      className="tw-self-stretch tw-flex tw-flex-row tw-items-center tw-italic tw-justify-between tw-text-text-dark-medium-importance"
    >
      <div className="tw-flex tw-flex-row tw-gap-2 tw-ml-[42px] tw-items-center">
        <div className="tw-text-wrap">
          {sub_item?.name ? sub_item?.name : 'PRODUCT_NAME'}
        </div>
      </div>

      <div className="tw-flex-shrink-0 tw-text-right tw-min-w-[80px]">
        {sub_item?.net_weight ? `${sub_item?.net_weight} t.` : 'nc.'}
      </div>
    </div>
  );
}

function SingleSiloDisplay({ row, bl }) {
  if (row.where_object) {
    return handleNewFormatForSilos(row, bl);
  }

  return handleOldFormatForSilosOrBags(row, bl);
}

const handleNewFormatForSilos = (row, bl) => {
  let siloDiv;

  if (row.where_object.is_replaced) {
    siloDiv = handleSiloIsReplacedDisplay(row);
  } else if (!row.where_object.is_planned) {
    siloDiv = handleSiloIsNotPlanned(row);
  } else {
    siloDiv = handleSiloDeliveredOrPlanned(row);
  }

  return (
    <div
      key={`${row.what}_${row.where}`}
      className="tw-flex tw-flex-row tw-gap-[5px]  tw-text-black tw-items-center tw-justify-start tw-align-center tw-grow"
    >
      <AgrivitechCheckbox
        isChecked={row.checked}
        icon={BL_STATUS.properties[bl.status].icon}
        style_checkbox={BL_STATUS.properties[bl.status].style_checkbox}
      />
      <div className="tw-rounded-8xs tw-w-full tw-bg-gainsboro-200 tw-overflow-x-auto tw-flex tw-flex-row tw-items-center tw-justify-between tw-py-0.5 tw-px-8 tw-gap-[20px]">
        <div className="tw-relative tw-tracking-[0.02em] tw-inline-block tw-min-w-[45px]">
          {row.what}
        </div>
        <div
          className="polygon"
          style={{
            ...(row.invert_arrow && {
              transform: 'rotate(270deg)',
            }),
          }}
        ></div>
        <div className="tw-relative tw-tracking-[0.02em] tw-inline-block tw-min-w-[37px] tw-text-text-dark-high-importance">
          {siloDiv}
        </div>
      </div>
    </div>
  );
};

const handleSiloIsReplacedDisplay = (row) => {
  return (
    <span className="tw-text-black">
      <span className="tw-line-through  tw-text-black/60">
        {row?.where_object?.text}
      </span>{' '}
      <span className=" tw-font-semibold">
        {row?.where_object?.replaced_by?.text}
      </span>
    </span>
  );
};

const handleSiloIsNotPlanned = (row) => {
  return (
    <span className="tw-text-black">{row.where_object.text} (Non prévu)</span>
  );
};

const handleSiloDeliveredOrPlanned = (row) => {
  return <span className="tw-text-black">{row.where_object.text}</span>;
};

const handleOldFormatForSilosOrBags = (row, bl) => {
  return (
    <div
      key={`${row.what}_${row.where}`}
      className="tw-flex tw-flex-row tw-gap-[5px]  tw-text-black tw-items-center tw-justify-start tw-align-center tw-grow"
    >
      <AgrivitechCheckbox
        isChecked={row.checked}
        icon={BL_STATUS.properties[bl.status].icon}
        style_checkbox={BL_STATUS.properties[bl.status].style_checkbox}
      />
      <div className="tw-rounded-8xs tw-w-full tw-bg-gainsboro-200 tw-overflow-x-auto tw-flex tw-flex-row tw-items-center tw-justify-between tw-py-0.5 tw-px-8 tw-gap-[20px]">
        <div className="tw-relative tw-tracking-[0.02em] tw-inline-block tw-min-w-[45px]">
          {row.what}
        </div>
        <div
          className="polygon"
          style={{
            ...(row.invert_arrow && {
              transform: 'rotate(270deg)',
            }),
          }}
        ></div>
        <div className="tw-relative tw-tracking-[0.02em] tw-inline-block tw-min-w-[37px] tw-text-text-dark-high-importance">
          {!row.where_substitute ? (
            <span className="tw-text-black">{row.where}</span>
          ) : null}

          {row.where_substitute && (
            <span className="tw-text-black">
              <span className="tw-line-through  tw-text-black/60">
                {row.where}
              </span>{' '}
              <span className=" tw-font-semibold">{row.where_substitute}</span>
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

function renderAllReservesForMoulinsDumee(item) {
  if (item?.client_id === 6 && item?.reserves?.length > 0) {
    return item.reserves
      .filter((reserve) => reserve?.additional_datas)
      .map((reserve, index) => (
        <div key={`reserve-${reserve.id || index}`}>
          {handleMoulinsDumeeMissingProductQuantity(reserve, item)}
        </div>
      ));
  }
  return null;
}

function anyMissingQuantityForMoulinsDumee(reserveAssociatedWithProduct, item) {
  const additionalData = reserveAssociatedWithProduct?.additional_datas;

  if (!additionalData) return false;

  if (item?.packaging === ALIMENT_TYPE_ENUM.SAC) {
    return (
      additionalData?.nb_bags_missing > 0 ||
      additionalData?.nb_bags_refused > 0 ||
      additionalData?.nb_bags_ripped > 0 ||
      additionalData?.nb_qty_delivered !== undefined
    );
  } else if (item?.packaging === ALIMENT_TYPE_ENUM.PICKING) {
    return (
      additionalData?.nb_qty_missing > 0 ||
      additionalData?.nb_qty_refused > 0 ||
      additionalData?.nb_qty_delivered !== undefined
    );
  } else if (item?.packaging === ALIMENT_TYPE_ENUM.VRAC) {
    return additionalData?.nb_qty_delivered !== undefined;
  }

  return false;
}

function MissingProductQuantity({
  reserveAssociatedWithProduct,
  silosPerCompartment,
  bl,
  item,
}) {
  if (item?.client_id === 6) {
    return null;
  }

  const blStatusValid =
    bl?.status === BL_STATUS.PARTIALLY_DELIVERED ||
    bl?.status === BL_STATUS.CANCELED;

  const dataInValidState =
    (silosPerCompartment &&
      silosPerCompartment.length > 0 &&
      silosPerCompartment?.[0]?.where_object) ||
    (reserveAssociatedWithProduct &&
      anyMissingQuantityForMoulinsDumee(reserveAssociatedWithProduct, item));

  if (!blStatusValid || !dataInValidState) {
    return;
  }

  if (silosPerCompartment && silosPerCompartment.length > 0) {
    return handleCooperlMissingProductQuantity({
      silosPerCompartment,
    });
  } else if (reserveAssociatedWithProduct?.id) {
    return handleMoulinsDumeeMissingProductQuantity(
      reserveAssociatedWithProduct,
      item
    );
  }
}

function handleCooperlMissingProductQuantity({ silosPerCompartment }) {
  const totalQuantity =
    silosPerCompartment?.[0]?.where_object?.total_quantity || 0.0;
  const missingQuantity =
    totalQuantity -
    silosPerCompartment?.reduce(
      (acc, row) =>
        acc +
        (row?.where_object?.replaced_by?.delivered_quantity ||
          row?.where_object?.delivered_quantity),
      0
    );

  <div className="tw-text-black/60 tw-pt-2 tw-flex tw-flex-row tw-flex-justify-between">
    Quantités non livrées : {missingQuantity?.toFixed(2)} t. /{' '}
    {totalQuantity?.toFixed(2)} t.
  </div>;
}

function handleMoulinsDumeeMissingProductQuantity(
  reserveAssociatedWithProduct,
  item
) {
  const additionalData = reserveAssociatedWithProduct?.additional_datas;

  const getQuantityInfo = () => {
    const packaging = item?.packaging;
    let plannedQuantity = '';
    let deliveredQuantity = '';
    let missingInfo = [];

    if (packaging === ALIMENT_TYPE_ENUM.VRAC) {
      plannedQuantity = `${item?.net_weight} ${item?.weight_unit}`;
      deliveredQuantity = additionalData?.nb_qty_delivered
        ? `${additionalData.nb_qty_delivered} ${item?.weight_unit}`
        : 'Non renseigné';
    } else if (packaging === ALIMENT_TYPE_ENUM.SAC) {
      plannedQuantity = `${item?.quantity} Sac(s)`;
      let deliveredSacs = item?.quantity || 0;

      if (additionalData?.nb_bags_missing > 0) {
        deliveredSacs -= additionalData.nb_bags_missing;
        missingInfo.push(
          `${additionalData.nb_bags_missing} manquant${additionalData.nb_bags_missing > 1 ? 's' : ''}`
        );
      }
      if (additionalData?.nb_bags_refused > 0) {
        deliveredSacs -= additionalData.nb_bags_refused;
        missingInfo.push(
          `${additionalData.nb_bags_refused} refusé${additionalData.nb_bags_refused > 1 ? 's' : ''}`
        );
      }
      if (additionalData?.nb_bags_ripped > 0) {
        deliveredSacs -= additionalData.nb_bags_ripped;
        missingInfo.push(
          `${additionalData.nb_bags_ripped} déchiré${additionalData.nb_bags_ripped > 1 ? 's' : ''} et refusé${additionalData.nb_bags_ripped > 1 ? 's' : ''}`
        );
      }

      deliveredQuantity = `${deliveredSacs} Sac(s)`;
    } else if (packaging === ALIMENT_TYPE_ENUM.PICKING) {
      plannedQuantity = `${item?.quantity} Unité(s)`;
      let deliveredUnits = item?.quantity || 0;

      if (additionalData?.nb_qty_missing > 0) {
        deliveredUnits -= additionalData.nb_qty_missing;
        missingInfo.push(
          `${additionalData.nb_qty_missing} manquant${additionalData.nb_qty_missing > 1 ? 's' : ''}`
        );
      }
      if (additionalData?.nb_qty_refused > 0) {
        deliveredUnits -= additionalData.nb_qty_refused;
        missingInfo.push(
          `${additionalData.nb_qty_refused} refusé${additionalData.nb_qty_refused > 1 ? 's' : ''}`
        );
      }

      deliveredQuantity = `${Number(deliveredUnits).toFixed(2)} Unité(s)`;
    }

    return { plannedQuantity, deliveredQuantity, missingInfo };
  };

  const { plannedQuantity, deliveredQuantity, missingInfo } = getQuantityInfo();

  return (
    <div className="tw-pt-2 tw-p-3 tw-bg-amber-100 tw-border tw-border-amber-300 tw-rounded tw-text-sm">
      <div className="tw-font-medium tw-text-amber-800 tw-mb-2">
        Informations de réserve
      </div>

      {plannedQuantity && (
        <div className="tw-mb-1 tw-text-gray-700">
          <span className="tw-font-medium">Quantité prévue :</span>{' '}
          {plannedQuantity}
        </div>
      )}

      {deliveredQuantity && (
        <div className="tw-mb-1 tw-text-gray-700">
          <span className="tw-font-medium">
            Quantité {item?.delivery_type === 'REPRISE' ? 'reprise' : 'livré'} :
          </span>{' '}
          {deliveredQuantity}
        </div>
      )}

      {missingInfo.length > 0 && (
        <div className="tw-mb-2 tw-text-red-600">
          <span className="tw-font-medium">Anomalies :</span>{' '}
          {missingInfo.join(', ')}
        </div>
      )}

      {(reserveAssociatedWithProduct?.uuid ||
        reserveAssociatedWithProduct.delivery_detail_key) && (
        <div className="tw-mt-2">
          <a
            href={`/reserves?query=${reserveAssociatedWithProduct.uuid || reserveAssociatedWithProduct.delivery_detail_key}&redirected=true`}
            className="tw-text-blue-600 tw-underline tw-text-xs hover:tw-text-blue-800"
          >
            Obtenir plus d'informations
          </a>
        </div>
      )}
    </div>
  );
}
